import {
  SELLER_ENTITY_LABELS_RU,
  SellerEntity,
  USER_DATE_INPUT_MAX,
  USER_DATE_INPUT_MIN,
} from '@crm/shared'
import type { RequestType } from '../../../shared/api/types'
import { AssistantOwnerSelect } from './AssistantOwnerSelect'
import { ClientCombobox, type ClientOption } from './ClientCombobox'

type AssistantManagers = Parameters<typeof AssistantOwnerSelect>[0]['managers']

// Карточка «Шапка заявки» на странице «Новая заявка»: менеджер-владелец (для
// помощника), клиент, тип, юрлицо продажи, дедлайн, заметка. Вынесено из
// NewRequestPage (≤500 строк).
export function NewRequestHeader(props: {
  isAssistant: boolean
  ownerManagerId: string
  assistantManagers: AssistantManagers
  onOwnerChange: (id: string) => void
  clientId: string
  /** Выбранный клиент целиком — поле показывает его, даже если его нет в списке. */
  selectedClient: ClientOption | null
  onClientChange: (client: ClientOption) => void
  onNewClient: () => void
  type: RequestType
  onTypeChange: (t: RequestType) => void
  /** Юрлицо продажи (п. 16 встречи 17.09) — дальше подставляется в КП и счёт. */
  sellerEntityId: SellerEntity
  onSellerEntityChange: (id: SellerEntity) => void
  deadline: string
  /** Текст ошибки года дедлайна — поле подсвечивается, «Создать заявку» неактивна. */
  deadlineError: string | null
  onDeadlineChange: (v: string) => void
  notes: string
  onNotesChange: (v: string) => void
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
      {props.isAssistant && (
        <AssistantOwnerSelect
          value={props.ownerManagerId}
          managers={props.assistantManagers}
          onChange={props.onOwnerChange}
        />
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-6">
        <div className="col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Клиент *</label>
          <div className="flex gap-1.5">
            <ClientCombobox
              clientId={props.clientId}
              selectedClient={props.selectedClient}
              onSelect={props.onClientChange}
            />
            <button
              type="button"
              onClick={props.onNewClient}
              title="Добавить нового клиента"
              className="shrink-0 px-2 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 hover:border-brand-400 transition-colors"
            >
              + Новый
            </button>
          </div>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Тип</label>
          <select
            value={props.type}
            onChange={(e) => props.onTypeChange(e.target.value as RequestType)}
            className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
          >
            <option value="TEKUCHKA">Текучка</option>
            <option value="PROJECT">Проект</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Юрлицо продажи</label>
          <select
            value={props.sellerEntityId}
            onChange={(e) => props.onSellerEntityChange(e.target.value as SellerEntity)}
            title="От этого юрлица по умолчанию выставятся КП и счёт"
            className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
          >
            {Object.values(SellerEntity).map((id) => (
              <option key={id} value={id}>
                {SELLER_ENTITY_LABELS_RU[id]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Дедлайн</label>
          <input
            type="date"
            value={props.deadline}
            min={USER_DATE_INPUT_MIN}
            max={USER_DATE_INPUT_MAX}
            aria-invalid={props.deadlineError ? true : undefined}
            onChange={(e) => props.onDeadlineChange(e.target.value)}
            className={`w-full px-2 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${props.deadlineError ? 'border-red-400' : 'border-gray-300'}`}
          />
          {props.deadlineError && (
            <p className="mt-1 text-xs text-red-600">{props.deadlineError}</p>
          )}
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Заметка</label>
          <input
            type="text"
            value={props.notes}
            onChange={(e) => props.onNotesChange(e.target.value)}
            placeholder="Необязательно..."
            className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
      </div>
    </div>
  )
}
